import { FunctionComponent, useContext, useState, useCallback, Suspense } from 'react';
import { useIntl } from 'dibs-react-intl';
import { getCurrency } from 'dibs-intl/exports/units';
import { graphql, useFragment } from 'react-relay';
import dibsCss from 'dibs-css';
import classNames from 'classnames';
import { useCheckoutSelector } from '../reducers/root';
import { getCheckoutParam } from '../helpers/checkoutRedirects';
import { PANES } from '../constants/checkoutConstants';
import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';
import CheckoutRestrictionAcknowledgement from './CheckoutRestrictionAcknowledgement';
import CheckoutSubmitButton from '../components/containers/CheckoutSubmitButton';
import { CheckoutBuyerMessage } from './CheckoutBuyerMessage';
import { CheckoutAddressView } from '../shared/CheckoutAddressView';
import CheckoutGifting from './CheckoutGifting';
import { CheckoutReviewStepDeliveryView } from './CheckoutReviewStepDeliveryView';
import CheckoutPaymentView from '../payment/CheckoutPaymentView';
import CheckoutReviewStepDetailsContainer from './CheckoutReviewStepDetailsContainer';
import { CheckoutReviewStepChargesDisclaimer } from './CheckoutReviewStepChargesDisclaimer';
import CheckoutTaxExemptionCheckbox from '../components/shared/CheckoutTaxExemptionCheckbox';
import CheckoutVatReverseChargeCheckbox from '../components/shared/CheckoutVatReverseChargeCheckbox';
import { CheckoutReturningBuyerDeliveryOptionsView } from './CheckoutReturningBuyerDeliveryOptionsView';
import { PreloadedDuties } from '../delivery/usePreloadedDuties';
import { useDisableShippingSubmit } from '../delivery/useDisableShippingSubmit';
import { CheckoutReviewStepStripeElements } from './CheckoutReviewStepStripeElements';
import { CheckoutStickySubmitButton } from './CheckoutStickySubmitButton';
import { isMwCheckoutOptimizationsTestActive } from '../helpers/mwCheckoutOptimizationsTestHelpers';
import { CheckoutDutiesMessage } from '../shared/helpers/CheckoutDutiesMessage';
import { useCustomsMessagingQuery } from '../hooks/useCustomsMessagingQuery';
import CheckoutSidebarPromotion from '../sidebar/promotion/CheckoutSidebarPromotion';
import { CheckoutReviewTally } from './CheckoutReviewTally';

import { CheckoutReviewStep_viewer$key } from './__generated__/CheckoutReviewStep_viewer.graphql';
import { CheckoutReviewStep_item$key } from './__generated__/CheckoutReviewStep_item.graphql';
import { CheckoutReviewStep_buyer$key } from './__generated__/CheckoutReviewStep_buyer.graphql';
import { CheckoutReviewStep_transaction$key } from './__generated__/CheckoutReviewStep_transaction.graphql';
import { CheckoutReviewStep_deliveryOptions$key } from './__generated__/CheckoutReviewStep_deliveryOptions.graphql';
import { CheckoutReviewStep_preCheckoutTransaction$key } from './__generated__/CheckoutReviewStep_preCheckoutTransaction.graphql';

import styles from './styles/CheckoutReviewStep.scss';

const viewerFragment = graphql`
    fragment CheckoutReviewStep_viewer on Viewer {
        ...CheckoutPaymentView_viewer
        ...CheckoutSubmitButton_viewer
        ...CheckoutTaxExemptionCheckbox_viewer
        ...CheckoutVatReverseChargeCheckbox_viewer
        ...CheckoutStickySubmitButton_viewer
        ...CheckoutDutiesMessage_viewer
        ...CheckoutSidebarPromotion_viewer
        isGiftingForAllVerticalsEnabled: featureFlag(feature: "giftingAllVerticals")
    }
`;

const itemFragment = graphql`
    fragment CheckoutReviewStep_item on Item {
        ...CheckoutSubmitButton_item
        ...CheckoutBuyerMessage_item
        ...CheckoutReviewStepDeliveryView_item
        ...CheckoutGifting_item
        ...CheckoutReturningBuyerDeliveryOptionsView_item
        ...CheckoutStickySubmitButton_item
        ...useCustomsMessagingQuery_item
        ...CheckoutSidebarPromotion_item
        ...CheckoutReviewTally_item
        vertical
    }
`;

const buyerFragment = graphql`
    fragment CheckoutReviewStep_buyer on User {
        ...CheckoutSubmitButton_buyer
        ...CheckoutPaymentView_buyer
        ...CheckoutReviewStepDetailsContainer_buyer
        ...CheckoutReturningBuyerDeliveryOptionsView_buyer
        ...CheckoutStickySubmitButton_buyer
        ...useCustomsMessagingQuery_buyer
        isTrade: isVerifiedTrade
        addressBook(partialAddressCheckoutEligibility: $partialAddressCheckoutEligibility) {
            addressList {
                serviceId
                ...CheckoutAddressView_address
            }
        }
    }
`;

const transactionFragment = graphql`
    fragment CheckoutReviewStep_transaction on Transaction {
        ...CheckoutReviewStepChargesDisclaimer_transaction
        ...CheckoutRestrictionAcknowledgement_transaction
        ...CheckoutSubmitButton_transaction
        ...CheckoutReviewStepDeliveryView_transaction
        ...CheckoutTaxExemptionCheckbox_transaction
        ...CheckoutGifting_transaction
        ...CheckoutVatReverseChargeCheckbox_transaction
        ...CheckoutReturningBuyerDeliveryOptionsView_transaction
        ...CheckoutStickySubmitButton_transaction
        ...CheckoutDutiesMessage_transaction
        ...CheckoutSidebarPromotion_transaction
        ...CheckoutReviewTally_transaction
        isGiftEligible
        buyerChargeCurrency
    }
`;

const deliveryOptionsFragment = graphql`
    fragment CheckoutReviewStep_deliveryOptions on ShipmentQuote @relay(plural: true) {
        ...CheckoutReviewStepDeliveryView_deliveryOptions
        ...useDisableShippingSubmit_deliveryOptions
        ...CheckoutReturningBuyerDeliveryOptionsView_deliveryOptions
    }
`;

const preCheckoutTransactionFragment = graphql`
    fragment CheckoutReviewStep_preCheckoutTransaction on Transaction {
        ...CheckoutReturningBuyerDeliveryOptionsView_preCheckoutTransaction
    }
`;

type Props = {
    isComputing: boolean;
    viewer: CheckoutReviewStep_viewer$key;
    item: CheckoutReviewStep_item$key;
    buyer: CheckoutReviewStep_buyer$key;
    transaction: CheckoutReviewStep_transaction$key;
    deliveryOptions: CheckoutReviewStep_deliveryOptions$key;
    preCheckoutTransaction: CheckoutReviewStep_preCheckoutTransaction$key | null;
    preloadedDuties: PreloadedDuties;
};

export const CheckoutReviewStep: FunctionComponent<Props> = props => {
    const { isComputing, preloadedDuties } = props;

    const viewer = useFragment(viewerFragment, props.viewer);
    const item = useFragment(itemFragment, props.item);
    const buyer = useFragment(buyerFragment, props.buyer);
    const transaction = useFragment(transactionFragment, props.transaction);
    const deliveryOptions = useFragment(deliveryOptionsFragment, props.deliveryOptions);
    const preCheckoutTransaction = useFragment(
        preCheckoutTransactionFragment,
        props.preCheckoutTransaction
    );
    const [isLoading, setIsLoading] = useState(false);

    const shippingAddressId = useCheckoutSelector(state => state.transaction.shippingAddressId);
    const isReturningBuyer = useCheckoutSelector(state => state.pane.isReturningBuyer);

    const { isMobile } = useContext(CheckoutServerVarsContext);

    const hasMakeOfferMessage = !!getCheckoutParam('message');

    const shippingAddress =
        !!shippingAddressId &&
        (buyer?.addressBook?.addressList || []).find(
            address => address?.serviceId === shippingAddressId
        );

    const isTradeBuyer = buyer?.isTrade;

    const isJewelrySeller = item?.vertical === 'jewelry';
    const isGiftingEnabled = isJewelrySeller || viewer?.isGiftingForAllVerticalsEnabled;
    const isGiftEligible = transaction?.isGiftEligible;

    const isShippingDisabled = useDisableShippingSubmit({ deliveryOptions, isLoading });
    const handleDeliveryOptionsStateChange = useCallback(
        ({ isLoading: isDeliveryOptionsLoading }: { isLoading: boolean }) => {
            setIsLoading(isDeliveryOptionsLoading);
        },
        []
    );

    const intl = useIntl();
    const defaultCurrency = getCurrency(intl.locale);
    const currency = transaction?.buyerChargeCurrency || defaultCurrency;
    const customsMessagingQueryReference = useCustomsMessagingQuery({ buyer, item });

    return (
        <div className={styles.reviewPaneSpacing}>
            <CheckoutReviewStepStripeElements>
                <div
                    className={classNames(styles.reviewDetailsContainer, {
                        [styles.reviewDetailsContainerBottomBorder]: !isGiftEligible,
                    })}
                >
                    {shippingAddress && (
                        <CheckoutReviewStepDetailsContainer
                            buyer={buyer}
                            pane={PANES.PANE_SHIPPING}
                            dataTn="shipping-address"
                        >
                            <CheckoutAddressView address={shippingAddress} />
                        </CheckoutReviewStepDetailsContainer>
                    )}
                    {!isReturningBuyer && (
                        <CheckoutReviewStepDetailsContainer
                            buyer={buyer}
                            pane={PANES.PANE_DELIVERY}
                            dataTn="shipping-method"
                        >
                            <CheckoutReviewStepDeliveryView
                                item={item}
                                deliveryOptions={deliveryOptions}
                                transaction={transaction}
                            />
                            {isMobile && isMwCheckoutOptimizationsTestActive() && (
                                <Suspense>
                                    <div className={dibsCss.sassyFontBodySmall}>
                                        <CheckoutDutiesMessage
                                            viewer={viewer}
                                            isComputing={isComputing}
                                            transaction={transaction}
                                            currency={currency}
                                            isMobile
                                            queryReference={customsMessagingQueryReference || null}
                                        />
                                    </div>
                                </Suspense>
                            )}
                        </CheckoutReviewStepDetailsContainer>
                    )}
                    <CheckoutReviewStepDetailsContainer
                        buyer={buyer}
                        pane={PANES.PANE_PAYMENT}
                        dataTn="payment-method"
                    >
                        <CheckoutPaymentView buyer={buyer} viewer={viewer} />
                    </CheckoutReviewStepDetailsContainer>
                    {isReturningBuyer && (
                        <CheckoutReviewStepDetailsContainer
                            buyer={buyer}
                            pane={PANES.PANE_DELIVERY}
                            dataTn="shipping-method"
                            disableChangeLink
                        >
                            <CheckoutReturningBuyerDeliveryOptionsView
                                isComputing={isComputing}
                                isLoading={isLoading}
                                item={item}
                                buyer={buyer}
                                transaction={transaction}
                                deliveryOptions={deliveryOptions}
                                preCheckoutTransaction={preCheckoutTransaction}
                                preloadedDuties={preloadedDuties}
                                onStateChange={handleDeliveryOptionsStateChange}
                            />
                            {isMobile && isMwCheckoutOptimizationsTestActive() && (
                                <Suspense>
                                    <div className={dibsCss.sassyFontBodySmall}>
                                        <CheckoutDutiesMessage
                                            viewer={viewer}
                                            isComputing={isComputing}
                                            transaction={transaction}
                                            currency={currency}
                                            isMobile
                                            queryReference={customsMessagingQueryReference || null}
                                        />
                                    </div>
                                </Suspense>
                            )}
                        </CheckoutReviewStepDetailsContainer>
                    )}
                </div>
                {isGiftingEnabled && (
                    <CheckoutGifting
                        isMobile={isMobile}
                        isComputing={isComputing}
                        item={item}
                        transaction={transaction}
                    />
                )}
                {hasMakeOfferMessage && <CheckoutBuyerMessage item={item} />}
                <CheckoutRestrictionAcknowledgement transaction={transaction} />
                {isMobile && !isMwCheckoutOptimizationsTestActive() && (
                    <>
                        <CheckoutTaxExemptionCheckbox
                            isMobile
                            isComputing={isComputing}
                            viewer={viewer}
                            transaction={transaction}
                        />
                        {isTradeBuyer && (
                            <CheckoutVatReverseChargeCheckbox
                                viewer={viewer}
                                transaction={transaction}
                                isComputing={isComputing}
                            />
                        )}
                    </>
                )}
                {(!isMobile || !isMwCheckoutOptimizationsTestActive()) && (
                    <CheckoutReviewStepChargesDisclaimer
                        transaction={transaction}
                        className={styles.chargesDisclaimer}
                    />
                )}
                {isMobile && isMwCheckoutOptimizationsTestActive() ? (
                    <>
                        <div
                            className={classNames(
                                dibsCss.borderB,
                                dibsCss.borderBSolid,
                                dibsCss.borderBDolphin,
                                dibsCss.pbSmall,
                                dibsCss.mbSmall,
                                dibsCss.sassyFontBodySizeSmall
                            )}
                        >
                            <CheckoutSidebarPromotion
                                viewer={viewer}
                                item={item}
                                transaction={transaction}
                                isComputing={isComputing}
                                trackingSource="Review Step"
                            />
                        </div>
                        <CheckoutReviewTally
                            item={item}
                            transaction={transaction}
                            isComputing={isComputing}
                        />
                        <CheckoutStickySubmitButton
                            viewer={viewer}
                            item={item}
                            buyer={buyer}
                            transaction={transaction}
                            isComputing={isComputing}
                            isLoading={isLoading}
                            isDisabled={isShippingDisabled}
                        />
                    </>
                ) : (
                    <div className={classNames(styles.submitCta, dibsCss.mbSmall)}>
                        <CheckoutSubmitButton
                            viewer={viewer}
                            item={item}
                            buyer={buyer}
                            transaction={transaction}
                            isComputing={isComputing}
                            isLoading={isLoading}
                            isDisabled={isShippingDisabled}
                        />
                    </div>
                )}
                {isMobile && isMwCheckoutOptimizationsTestActive() && (
                    <>
                        <CheckoutReviewStepChargesDisclaimer
                            transaction={transaction}
                            className={styles.chargesDisclaimer}
                        />
                        <CheckoutTaxExemptionCheckbox
                            isMobile
                            isComputing={isComputing}
                            viewer={viewer}
                            transaction={transaction}
                        />
                        {isTradeBuyer && (
                            <CheckoutVatReverseChargeCheckbox
                                viewer={viewer}
                                transaction={transaction}
                                isComputing={isComputing}
                            />
                        )}
                    </>
                )}
            </CheckoutReviewStepStripeElements>
        </div>
    );
};
